/**
 * @fileoverview Column definitions for the POD claims schedule.
 *
 * Purpose: The creditor schedule proper — the books-and-records estimate of
 * what is owed beside the proof as lodged, split into the classes it ranks in
 * (security held, preferential, unsecured), then the adjudication outcome and
 * what was finally admitted for dividend.
 *
 * Pure presentational. Wired up by PodClaimsPanel.tsx.
 */

import { type ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/ui/primitives/badge";
import { Checkbox } from "@/ui/primitives/checkbox";
import { SortableHeader } from "@/ui/composed/data-display/sortable-header";
import { RelatedPartyBadge } from "../_shared/RelatedPartyBadge";
import { isNewOutput } from "../_shared/use-new-output-boundary";
import { NewOutputBadge } from "../_shared/new-output-badge";
import { PodClaimExportMenu } from "./PodClaimExportMenu";
import {
  POD_CREDITOR_TYPE_COLORS,
  POD_CREDITOR_TYPE_LABELS,
  POD_STATUS_COLORS,
  POD_STATUS_LABELS,
  derivePodOutcome,
  derivePodProofSplit,
  fmtMoneyShort,
  type PodClaimUI,
} from "./podShared";
import { Money } from "./podClaimsColumns.cells";

/** Id of the column pinned to the left edge while the rest scrolls. */
export const POD_CLAIMS_STICKY_COLUMN = "creditorName";

/** Id of the row-selection checkbox column, pinned before the creditor. */
export const POD_CLAIMS_SELECT_COLUMN = "select";

/** Id of the trailing per-row export action column. */
export const POD_CLAIMS_EXPORT_COLUMN = "export";

/** Ids of the columns that carry money, so the panel can align them as one. */
export const POD_CLAIMS_MONEY_COLUMNS = new Set([
  "estimatedValue",
  "securityHeld",
  "preferred",
  "unsecured",
  "totalProof",
  "totalAdmitted",
]);

/**
 * Admitted / rejected only mean something once the claim has actually been
 * determined — a claim on hold or awaiting more information carries no
 * admitted figure, only an intention to come back to it.
 */
function isDetermined(claim: PodClaimUI): boolean {
  return (
    claim.status === "adjudicated" &&
    claim.recommendation !== "hold" &&
    claim.recommendation !== "more_information"
  );
}

/**
 * Builds the POD claims schedule columns.
 *
 * @param newSince - Boundary from `useNewOutputBoundary`; creditor names on
 *   claims created after it get the amber "new since viewed" underline.
 */
// eslint-disable-next-line react-refresh/only-export-components -- false positive: react-refresh 0.4.26 reads the ALL_CAPS POD_CLAIMS_MONEY_COLUMNS Set above as a component, so it treats this file as a component module; it exports no component
export function makePodClaimsColumns(
  newSince: number | null,
): Array<ColumnDef<PodClaimUI>> {
  return [
    {
      id: POD_CLAIMS_SELECT_COLUMN,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllRowsSelected()
              ? true
              : table.getIsSomeRowsSelected()
                ? "indeterminate"
                : false
          }
          onCheckedChange={(value) =>
            table.toggleAllRowsSelected(value === true)
          }
          aria-label="Select all claims"
        />
      ),
      meta: { label: "Select" },
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(value === true)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${row.original.creditorName}`}
        />
      ),
    },
    {
      id: POD_CLAIMS_STICKY_COLUMN,
      accessorFn: (claim) => claim.creditorName,
      header: ({ column }) => (
        <SortableHeader column={column} label="Creditor" />
      ),
      meta: { label: "Creditor" },
      enableHiding: false,
      sortingFn: (a, b) =>
        a.original.creditorName.localeCompare(b.original.creditorName),
      cell: ({ row }) => {
        // `truncate` needs a block-level box to clamp against, otherwise a long
        // creditor name overflows into the next column.
        const isNew = isNewOutput(row.original._creationTime, newSince);
        return (
          <div className="flex min-w-0 items-center gap-2">
            <span
              className="min-w-0 truncate text-sm font-semibold text-foreground"
              title={row.original.creditorName}
            >
              {row.original.creditorName}
            </span>
            {isNew && <NewOutputBadge />}
          </div>
        );
      },
    },
    {
      id: "creditorType",
      accessorFn: (claim) => claim.creditorType,
      header: "Type",
      meta: { label: "Type" },
      cell: ({ row }) => (
        <div className="flex flex-wrap items-center gap-1">
          <Badge
            color={POD_CREDITOR_TYPE_COLORS[row.original.creditorType]}
            variant="soft"
            size="sm"
          >
            {POD_CREDITOR_TYPE_LABELS[row.original.creditorType]}
          </Badge>
          {row.original.relatedParty && (
            <RelatedPartyBadge match={row.original.relatedParty} />
          )}
        </div>
      ),
    },
    {
      id: "estimatedValue",
      accessorFn: (claim) => claim.estimatedValue ?? null,
      header: ({ column }) => (
        <SortableHeader column={column} label="Est. Value" />
      ),
      meta: { label: "Est. Value" },
      cell: ({ row }) => {
        const claim = row.original;
        // The books figure is only a second opinion on the claim when the row it
        // came from was recorded independently of it.
        const fromPod =
          claim.estimatedValueSource === "pod" ||
          claim.estimatedValueSource === "mixed";
        return (
          <Money
            value={claim.estimatedValue ?? null}
            muted
            flagged={fromPod}
            note={
              claim.estimatedValueSource === "pod"
                ? "from POD"
                : claim.estimatedValueSource === "mixed"
                  ? "partly from POD"
                  : undefined
            }
            title={
              fromPod
                ? "What the creditors table records as owing — but it was written from this creditor's proof of debt, so it restates the claim rather than corroborating it"
                : "What the creditors table records as owing"
            }
          />
        );
      },
    },
    {
      id: "securityHeld",
      accessorFn: (claim) => derivePodProofSplit(claim).securityHeld,
      header: ({ column }) => (
        <SortableHeader column={column} label="Security Held" />
      ),
      meta: { label: "Security Held" },
      cell: ({ row }) => (
        <Money
          value={derivePodProofSplit(row.original).securityHeld}
          title={
            row.original.securityHeldDescription ??
            "The creditor's own estimate of the value of security they hold"
          }
        />
      ),
    },
    {
      id: "preferred",
      accessorFn: (claim) => derivePodProofSplit(claim).preferred,
      header: ({ column }) => (
        <SortableHeader column={column} label="Preferred Proof" />
      ),
      meta: { label: "Preferred Proof" },
      cell: ({ row }) => (
        <Money value={derivePodProofSplit(row.original).preferred} />
      ),
    },
    {
      id: "unsecured",
      accessorFn: (claim) => derivePodProofSplit(claim).unsecured,
      header: ({ column }) => (
        <SortableHeader column={column} label="Unsecured Proof" />
      ),
      meta: { label: "Unsecured Proof" },
      cell: ({ row }) => (
        <Money value={derivePodProofSplit(row.original).unsecured} />
      ),
    },
    {
      id: "totalProof",
      accessorFn: (claim) => claim.claimedTotal,
      header: ({ column }) => (
        <SortableHeader column={column} label="Total Proof" />
      ),
      meta: { label: "Total Proof" },
      cell: ({ row }) => {
        const total = derivePodProofSplit(row.original).total;
        return <Money value={total > 0 ? total : null} bold />;
      },
    },
    {
      id: "status",
      accessorFn: (claim) => claim.status,
      header: "Status",
      meta: { label: "Status" },
      cell: ({ row }) => {
        const claim = row.original;
        // Variance only means something once supporting docs have arrived.
        const showAmounts =
          claim.status === "received" || claim.status === "adjudicated";
        const variance = claim.claimedTotal - claim.substantiatedTotal;
        const unproven =
          showAmounts && claim.claimedTotal > 0 && variance > 0.005;

        return (
          <>
            <Badge
              color={POD_STATUS_COLORS[claim.status]}
              variant="soft"
              size="sm"
            >
              {POD_STATUS_LABELS[claim.status]}
            </Badge>
            {/* The schedule carries no variance column, so the one thing it must
              not lose is that the documents don't back the claim — it is the
              reason to open the claim at all. */}
            {unproven && (
              <p
                className="mt-1 font-mono text-[10px] tabular-nums leading-tight text-red-700 dark:text-red-400"
                title="Supporting documents fall short of the amount claimed"
              >
                {fmtMoneyShort(variance)} unproven
              </p>
            )}
          </>
        );
      },
    },
    {
      id: "adjudication",
      accessorFn: (claim) => claim.recommendation ?? "",
      header: "Adjudication",
      meta: { label: "Adjudication" },
      cell: ({ row }) => {
        const claim = row.original;
        const outcome = derivePodOutcome(claim);
        if (!outcome) {
          return <span className="text-sm text-muted-foreground">—</span>;
        }
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge color={outcome.color} variant="soft" size="sm">
              {outcome.label}
            </Badge>
            {/* Only the rejected figure here — the admitted one has its own
              column, and repeating it reads as two different numbers. */}
            {(claim.rejectedAmount ?? 0) > 0 && (
              <span className="font-mono text-xs tabular-nums text-red-700 dark:text-red-400">
                −{fmtMoneyShort(claim.rejectedAmount ?? 0)}
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: "totalAdmitted",
      accessorFn: (claim) =>
        isDetermined(claim) ? (claim.admittedAmount ?? 0) : null,
      header: ({ column }) => (
        <SortableHeader column={column} label="Total Admitted" />
      ),
      meta: { label: "Total Admitted" },
      cell: ({ row }) => {
        // What actually ranks for dividend once the claim has been determined.
        // Blank until then, so a nil admission never reads the same as a claim
        // still on hold or waiting on more information.
        const determined = isDetermined(row.original);
        return (
          <Money
            value={determined ? (row.original.admittedAmount ?? 0) : null}
            bold
            title={
              determined
                ? "Admitted for dividend on adjudication"
                : "No determination yet"
            }
          />
        );
      },
    },
    {
      id: POD_CLAIMS_EXPORT_COLUMN,
      header: "",
      meta: { label: "Export" },
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <PodClaimExportMenu
          jobId={row.original.jobId}
          claimIds={[row.original._id]}
          variant="icon"
        />
      ),
    },
  ];
}
